import React, { useEffect, useRef, useState } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import { fromLonLat } from 'ol/proj';
import { Icon, Style, Stroke, Fill, Text, Circle as CircleStyle } from 'ol/style';
import { Cluster } from 'ol/source';
import Overlay from 'ol/Overlay';
import 'ol/ol.css';
import { MAP_CONFIG } from '../../config/mapConfig';

/**
 * Componente de mapa con OpenLayers
 *
 * Muestra ubicaciones de trabajadores en tiempo real con marcadores personalizados
 */
const OpenLayersMap = ({ workers, selectedWorker, onWorkerClick, showRoute, routeData }) => {
  const mapRef = useRef();
  const mapInstanceRef = useRef();
  const vectorLayerRef = useRef();
  const routeLayerRef = useRef();
  const popupRef = useRef();
  const popupOverlayRef = useRef();
  const [selectedMarker, setSelectedMarker] = useState(null);

  // Inicializar mapa
  useEffect(() => {
    if (!mapInstanceRef.current) {
      // Crear fuentes de datos
      const vectorSource = new VectorSource();

      // Configurar clustering si está habilitado
      let markerSource = vectorSource;
      if (MAP_CONFIG.clustering.enabled) {
        markerSource = new Cluster({
          distance: MAP_CONFIG.clustering.distance,
          source: vectorSource,
          minDistance: MAP_CONFIG.clustering.minPoints
        });
      }

      const vectorLayer = new VectorLayer({
        source: markerSource,
        style: createMarkerStyle
      });

      // Capa para rutas
      const routeSource = new VectorSource();
      const routeLayer = new VectorLayer({
        source: routeSource,
        style: new Style({
          stroke: new Stroke({
            color: MAP_CONFIG.polylineStyle.strokeColor,
            width: MAP_CONFIG.polylineStyle.strokeWeight
          })
        })
      });

      // Crear mapa
      const map = new Map({
        target: mapRef.current,
        layers: [
          new TileLayer({
            source: new OSM() // OpenStreetMap como capa base (gratuito)
          }),
          routeLayer,
          vectorLayer
        ],
        view: new View({
          center: fromLonLat([MAP_CONFIG.defaultCenter.lng, MAP_CONFIG.defaultCenter.lat]),
          zoom: MAP_CONFIG.defaultZoom,
          maxZoom: MAP_CONFIG.maxZoom,
          minZoom: MAP_CONFIG.minZoom
        })
      });

      // Crear overlay para popup
      const popup = document.createElement('div');
      popup.className = 'ol-popup';
      popup.ref = popupRef.current;

      const popupOverlay = new Overlay({
        element: popup,
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [0, -10]
      });

      map.addOverlay(popupOverlay);

      // Click en marcadores
      map.on('click', (evt) => {
        const feature = map.forEachFeatureAtPixel(evt.pixel, (feature) => feature);

        if (feature) {
          const features = feature.get('features'); // Si es un cluster

          if (features && features.length > 1) {
            // Es un cluster, hacer zoom
            const extent = vectorSource.getExtent();
            map.getView().fit(extent, { duration: 1000, padding: [50, 50, 50, 50] });
          } else {
            // Es un marcador individual
            const worker = features ? features[0].get('worker') : feature.get('worker');

            if (worker) {
              showPopup(worker, evt.coordinate, popupOverlay, popup);
              if (onWorkerClick) {
                onWorkerClick(worker);
              }
            }
          }
        } else {
          // Cerrar popup si se hace click fuera
          popupOverlay.setPosition(undefined);
          setSelectedMarker(null);
        }
      });

      // Cambiar cursor al pasar sobre marcadores
      map.on('pointermove', (evt) => {
        const hit = map.hasFeatureAtPixel(evt.pixel);
        map.getTargetElement().style.cursor = hit ? 'pointer' : '';
      });

      mapInstanceRef.current = map;
      vectorLayerRef.current = vectorLayer;
      routeLayerRef.current = routeLayer;
      popupOverlayRef.current = popupOverlay;
    }
  }, []);

  // Actualizar marcadores cuando cambien los trabajadores
  useEffect(() => {
    if (!vectorLayerRef.current || !workers || workers.length === 0) return;

    const vectorLayer = vectorLayerRef.current;
    const source = MAP_CONFIG.clustering.enabled
      ? vectorLayer.getSource().getSource() // Si hay clustering, obtener fuente interna
      : vectorLayer.getSource();

    source.clear();

    workers.forEach(worker => {
      if (worker.latitud && worker.longitud) {
        const feature = new Feature({
          geometry: new Point(fromLonLat([worker.longitud, worker.latitud])),
          worker: worker
        });

        source.addFeature(feature);
      }
    });
  }, [workers]);

  // Zoom a trabajador seleccionado
  useEffect(() => {
    if (!mapInstanceRef.current || !selectedWorker) return;

    const map = mapInstanceRef.current;
    const view = map.getView();

    if (selectedWorker.latitud && selectedWorker.longitud) {
      view.animate({
        center: fromLonLat([selectedWorker.longitud, selectedWorker.latitud]),
        zoom: 16,
        duration: 1000
      });
    }
  }, [selectedWorker]);

  // Mostrar ruta si se proporciona
  useEffect(() => {
    if (!routeLayerRef.current) return;

    const routeSource = routeLayerRef.current.getSource();
    routeSource.clear();

    if (showRoute && routeData && routeData.ruta && routeData.ruta.length > 0) {
      // Convertir coordenadas a proyección del mapa
      const coordinates = routeData.ruta.map(coord => fromLonLat(coord));

      const routeFeature = new Feature({
        geometry: new LineString(coordinates)
      });

      routeSource.addFeature(routeFeature);

      // Ajustar vista para mostrar toda la ruta
      const extent = routeSource.getExtent();
      mapInstanceRef.current.getView().fit(extent, {
        padding: [50, 50, 50, 50],
        duration: 1000
      });
    }
  }, [showRoute, routeData]);

  /**
   * Crear estilo de marcador según estado del trabajador
   */
  function createMarkerStyle(feature) {
    const features = feature.get('features'); // Si es un cluster

    if (features && features.length > 1) {
      // Estilo para clusters
      return new Style({
        image: new CircleStyle({
          radius: 20,
          fill: new Fill({ color: '#3399CC' }),
          stroke: new Stroke({ color: '#fff', width: 2 })
        }),
        text: new Text({
          text: features.length.toString(),
          fill: new Fill({ color: '#fff' }),
          font: 'bold 14px sans-serif'
        })
      });
    }

    // Estilo para marcador individual
    const worker = features ? features[0].get('worker') : feature.get('worker');

    if (!worker) return null;

    const color = MAP_CONFIG.markerColors[worker.estado] || MAP_CONFIG.markerColors.offline;

    return new Style({
      image: new CircleStyle({
        radius: 10,
        fill: new Fill({ color }),
        stroke: new Stroke({ color: '#fff', width: 2 })
      }),
      text: new Text({
        text: worker.nombre_completo || worker.nombre || '',
        offsetY: 20,
        fill: new Fill({ color: '#000' }),
        stroke: new Stroke({ color: '#fff', width: 3 }),
        font: '12px sans-serif'
      })
    });
  }

  /**
   * Mostrar popup con información del trabajador
   */
  function showPopup(worker, coordinate, overlay, element) {
    setSelectedMarker(worker);

    const estado = worker.estado === 'online' ? 'En línea' :
                  worker.estado === 'idle' ? 'Inactivo' : 'Desconectado';

    const velocidad = worker.velocidad ? `${(worker.velocidad * 3.6).toFixed(1)} km/h` : 'N/A';

    const ultimaActualizacion = worker.segundos_desde_actualizacion
      ? formatearTiempo(worker.segundos_desde_actualizacion)
      : 'Ahora';

    element.innerHTML = `
      <div class="worker-popup">
        <div class="worker-popup-header">
          <strong>${worker.nombre_completo || `${worker.nombre} ${worker.apellido}`}</strong>
          <span class="status-badge status-${worker.estado}">${estado}</span>
        </div>
        <div class="worker-popup-body">
          <p><strong>Velocidad:</strong> ${velocidad}</p>
          <p><strong>Última actualización:</strong> ${ultimaActualizacion}</p>
          <p><strong>Ubicación:</strong> ${worker.latitud.toFixed(6)}, ${worker.longitud.toFixed(6)}</p>
        </div>
      </div>
    `;

    overlay.setPosition(coordinate);
  }

  /**
   * Formatear segundos a texto legible
   */
  function formatearTiempo(segundos) {
    if (segundos < 60) return 'Hace unos segundos';
    if (segundos < 3600) return `Hace ${Math.floor(segundos / 60)} minutos`;
    return `Hace ${Math.floor(segundos / 3600)} horas`;
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      />
      <div ref={popupRef} className="ol-popup" />

      <style jsx>{`
        .worker-popup {
          background: white;
          border-radius: 8px;
          padding: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          min-width: 200px;
        }
        .worker-popup-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e0e0e0;
        }
        .worker-popup-body p {
          margin: 4px 0;
          font-size: 13px;
        }
        .status-badge {
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }
        .status-online {
          background: #4caf50;
          color: white;
        }
        .status-idle {
          background: #ffc107;
          color: #000;
        }
        .status-offline {
          background: #9e9e9e;
          color: white;
        }
      `}</style>
    </div>
  );
};

export default OpenLayersMap;
